/**
 * Map style presets. KidzioMap accepts a `basemap` prop to switch between them.
 *
 *   - "voyager"   — CartoDB Voyager raster (default; cartographic, clean).
 *   - "satellite" — Esri World Imagery raster (aerial photos, "real" view).
 *
 * Both are free + no-API-key for our scale. For production traffic, swap in
 * MapTiler / Stadia Maps / self-hosted vector tiles via this same export.
 *
 * Attributions are required and rendered on the map by MapLibre's standard
 * AttributionControl.
 */
export type KidzioBasemap = 'voyager' | 'satellite';

/** Glyph atlas used by every symbol layer (e.g. geofence label text).
 *  Required by MapLibre for any `text-field` rendering — without it the
 *  layer either fails silently or falls back to a tofu glyph. Using the
 *  OpenMapTiles font CDN (free, no API key); swap to a self-hosted set
 *  for production if traffic gets noisy. */
const GLYPHS_URL = 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf';

export const VOYAGER_STYLE = {
  version: 8 as const,
  glyphs: GLYPHS_URL,
  sources: {
    basemap: {
      type: 'raster' as const,
      tiles: [
        'https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
        'https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
        'https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
        'https://d.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
      ],
      tileSize: 256,
      maxzoom: 20,
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors · © <a href="https://carto.com/attributions">CARTO</a>',
    },
  },
  layers: [{ id: 'basemap', type: 'raster' as const, source: 'basemap', minzoom: 0, maxzoom: 22 }],
};

// Esri World Imagery — aerial / satellite. Free attribution-only tier.
export const SATELLITE_STYLE = {
  version: 8 as const,
  glyphs: GLYPHS_URL,
  sources: {
    basemap: {
      type: 'raster' as const,
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      maxzoom: 19,
      attribution:
        'Tiles © <a href="https://www.esri.com">Esri</a> — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
    },
  },
  layers: [{ id: 'basemap', type: 'raster' as const, source: 'basemap', minzoom: 0, maxzoom: 22 }],
};

export function getBasemap(name: KidzioBasemap) {
  return name === 'satellite' ? SATELLITE_STYLE : VOYAGER_STYLE;
}

// Backwards compat for the old import name.
export const KIDZIO_DEFAULT_STYLE = VOYAGER_STYLE;
